import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { getReloadYard } from "../../graphql/reloadyard/custom-queries";

import {
  CircularProgress,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";

import { useNotify, Title } from "react-admin";

import { YardSetupIcon } from "../../components/common/iconography";
import { ImageField } from "../../components/common/ImageField";
import CrewCard from "../../components/common/CrewCard";
import { ClaimLoadPanel } from "../resources/loadSearch/ClaimLoadPage";

import { logError, setupCommodityMaps } from "../../utils/appUtils";
import {
  buildCarrierSession,
  getCarrierYardOptions,
  selectCarrierJunctionForYard,
} from "../../utils/carrierUserUtils";
import { useUser } from "../../authenticatorApp/UserContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(1),
  },
  cardAvatar: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  centerLogo: {
    display: "flex",
    justifyContent: "center", // Center horizontally
    alignItems: "center", // Center vertically
    // height: '100%',
  },
  claimPanel: {
    marginTop: theme.spacing(2),
  },
  // The card supplies no top padding of its own — the header used to — and an
  // outlined label overhangs its field, so the switcher needs this much room.
  switcherBody: {
    paddingTop: theme.spacing(1),
  },
  // overline carries a 2.66 line height of its own, which would stretch the
  // line it now shares with its value.
  contactLabel: {
    lineHeight: "inherit",
    marginRight: theme.spacing(0.5),
  },
  cardWithBackdrop: {
    position: "relative",
    overflow: "hidden",
  },
  localBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: theme.zIndex.modal,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const sendFeatureRequestEmail = (appUser, message) => {
  const { firstName, lastName, email, accountName, reloadYard } = appUser;
  const htmlContent = `
    <p><strong>From:</strong> ${firstName} ${lastName} &lt;${email}&gt;</p>
    <p><strong>Account:</strong> ${accountName}</p>
    <p><strong>Yard:</strong> ${reloadYard?.name}</p>
    <hr/>
    <div style="white-space: pre-line;">${message}</div>
  `;
  return API.post("transREST", "/sendEmail", {
    body: {
      to: "support@transloads.co",
      subject: "Feature Request from Carrier Portal",
      template: "emailTemplate.html",
      htmlContent,
    },
  });
};

export const CarrierDashboard = (props) => {
  const classes = useStyles();
  const { appUser, setAppUser, isMultiTenant } = useUser();

  return (
    <div className={classes.root}>
      <Title title=" " />
      <Grid container>
        <Grid item xs={12} id="yard-select">
          {isMultiTenant() ? (
            <ReloadYardsCard
              appUser={appUser}
              setAppUser={setAppUser}
              classes={classes}
            />
          ) : (
            <ReloadYardCard appUser={appUser} classes={classes} />
          )}
        </Grid>
        <Grid item xs={12}>
          <ClaimLoadPanel className={classes.claimPanel} />
        </Grid>
      </Grid>
    </div>
  );
};

// The contact values are short enough to sit beside their labels; a line each
// made the details column taller than the logo it sits next to.
const ContactLine = ({ label, value, classes }) => (
  <Typography variant="body2">
    <Typography
      variant="overline"
      component="span"
      className={classes.contactLabel}
    >
      {label}:
    </Typography>
    {value}
  </Typography>
);

const YardContactDetails = ({ reloadYard, classes }) => (
  <>
    <Typography variant="overline">Address:</Typography>
    <Typography variant="body2">{reloadYard.street}</Typography>
    <Typography variant="body2">{`${reloadYard.city}, ${reloadYard.state} ${reloadYard.zipCode}, ${reloadYard.country}`}</Typography>
    <ContactLine label="Email" value={reloadYard.email} classes={classes} />
    <ContactLine label="Phone" value={reloadYard.phone} classes={classes} />
    <ContactLine label="Fax" value={reloadYard.fax} classes={classes} />
  </>
);

const ReloadYardCard = ({ appUser, classes, ...props }) => {
  const [reloadYard] = useState(appUser?.reloadYard);

  if (!reloadYard) return <></>;
  return (
    <CrewCard
      showDivider={false}
      title={reloadYard.name}
      icon={<YardSetupIcon fontSize="large" />}
      addMargin={true}
    >
      <Grid container spacing={2}>
        <Grid item xs={6} className={classes.centerLogo}>
          <ImageField
            source="logoImage"
            record={reloadYard}
            // 30% off the 300x160 box. scaleThumb fits the logo inside both
            // bounds, so the width has to come down with the height or a wide
            // logo stays width-bound and never shrinks.
            thumbWidth={210}
            thumbHeight={112}
            scaleThumb={true}
          />
        </Grid>
        <Grid item xs={6}>
          <YardContactDetails reloadYard={reloadYard} classes={classes} />
        </Grid>
      </Grid>
    </CrewCard>
  );
};

const ReloadYardsCard = ({ appUser, setAppUser, classes, ...props }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  // A carrier user's yards come from their junctions, one per yard. This card
  // began as a copy of the customer dashboard's, which reads reloadYardAccounts
  // — a field no carrier user has, so it threw on first render. Every yard the
  // person works in is somewhere they may go.
  const [reloadYards] = useState(() =>
    getCarrierYardOptions(appUser?.reloadYardCarriers?.items, appUser?.appType),
  );
  const reloadYard = appUser?.reloadYard;

  const handleYardChange = async (event) => {
    try {
      setLoading(true);
      const reloadYardId = event.target.value;

      // Looked up strictly, unlike login's selectCarrierJunction: falling back
      // to some other yard is the right answer when the alternative is no
      // portal at all, and the wrong one when the user named the yard they want.
      const reloadYardCarrier = selectCarrierJunctionForYard(
        appUser.reloadYardCarriers?.items,
        reloadYardId,
        appUser.appType,
        appUser.carrierId,
      );
      if (!reloadYardCarrier) {
        throw new Error(
          `No usable carrier record for transload ${reloadYardId}`,
        );
      }

      const reloadYardResponse = await API.graphql(
        graphqlOperation(getReloadYard, { id: reloadYardId }),
      );
      const nextReloadYard = reloadYardResponse.data.getReloadYard;
      // Commodity maps only, matching setupCarrierData — the carrier portal
      // never reads the yard's locations.
      setupCommodityMaps(nextReloadYard);

      setAppUser({
        ...appUser,
        reloadYard: nextReloadYard,
        // The new yard's permissions travel with it: a user can be dispatch at
        // one yard and something narrower at the next.
        ...buildCarrierSession(reloadYardCarrier),
      });
    } catch (err) {
      logError(
        "CarrierDashboard:ReloadYardsCard::handleYardChange",
        err,
        notify,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    // No header: with several yards the switcher is what this card is for, and
    // the avatar-and-title row only pushed it further down the page.
    <CrewCard showDivider={false} addMargin={true}>
      <Grid container spacing={2} className={classes.switcherBody}>
        <Grid item xs={6}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                id="site-select"
                label="Transload"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={reloadYard.id}
                onChange={handleYardChange}
                helperText="Select a transload to manage"
              >
                {reloadYards.map((yard) => (
                  <MenuItem key={yard.id} value={yard.id}>
                    {yard.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} className={classes.centerLogo}>
              {loading ? (
                <CircularProgress color="inherit" />
              ) : (
                <ImageField
                  source="logoImage"
                  record={reloadYard}
                  // 30% off the 300x105 box, width included — see ReloadYardCard.
                  thumbWidth={210}
                  thumbHeight={74}
                  scaleThumb={true}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <YardContactDetails reloadYard={reloadYard} classes={classes} />
        </Grid>
      </Grid>
    </CrewCard>
  );
};
